import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, type ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';

import {
  Alert,
  Card,
  Input,
  OptionCard,
  RadioGroup,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { useServiceability } from '@/lib/api/catalogue';
import type { AddressOut } from '@/lib/api/types';
import { LocationPicker, type PickedLocation } from '@/features/location/LocationPicker';
import { addressSchema, normalisePincode, type AddressFormValues } from '@/lib/validators';

import { PincodeLookup } from './PincodeLookup';
import { PrintingHoursNotice } from './PrintingHoursNotice';

/** The sentinel the picker uses for "type a new address". */
export const NEW_ADDRESS = 'new';

export interface AddressFormProps {
  /** Wired to the sticky submit button via `form="..."`, outside the form element. */
  formId: string;
  /** Saved addresses, when the customer is signed in. Empty for a new customer. */
  saved: AddressOut[];
  savedLoading: boolean;
  /** Which saved address is selected, or NEW_ADDRESS. Owned by the step. */
  selectedAddressId: string;
  onSelectAddress: (id: string) => void;
  /** Reported upward so the step can price delivery for it. */
  onPincodeChange: (pincode: string | null) => void;
  onValidSubmit: (values: AddressFormValues) => void;
  /** Field messages from a server 422, keyed by field name. */
  serverFieldErrors?: Record<string, string>;
  disabled?: boolean;
  /** Prefilled from the signed-in profile, so the phone box is not empty. */
  defaultPhone?: string;
  defaultName?: string;
  /** The pin. Required before the step's Continue is usable — see AddressStep. */
  location: PickedLocation | null;
  onLocationChange: (location: PickedLocation) => void;
}

/**
 * The delivery address, PIN CODE FIRST.
 *
 * The ordering is the requirement and it drives the layout: the PIN code is its
 * own card at the top, the serviceability answer lands under it, and the rest of
 * the fields only appear once we know we can deliver. Asking for a house number
 * before answering "do you even come here?" wastes the customer's time and ours.
 *
 * Validation is react-hook-form + zod for instant feedback, and the server's 422
 * is still handled — `serverFieldErrors` is merged into the same `error` slots,
 * so a rule only the backend knows about (a PIN code we stopped serving this
 * morning) surfaces on the field it belongs to, not in a banner at the top.
 */
export function AddressForm({
  formId,
  saved,
  savedLoading,
  selectedAddressId,
  onSelectAddress,
  onPincodeChange,
  onValidSubmit,
  serverFieldErrors = {},
  disabled = false,
  defaultPhone = '',
  defaultName = '',
  location,
  onLocationChange,
}: AddressFormProps) {
  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    mode: 'onBlur',
    defaultValues: {
      name: defaultName,
      phone: defaultPhone.replace(/^\+91/, ''),
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
    },
  });

  const { register, watch, setValue, formState, handleSubmit } = form;
  const pincode = watch('pincode');
  const pincodeComplete = /^[1-9]\d{5}$/.test(pincode ?? '');

  const serviceabilityQuery = useServiceability(pincodeComplete ? pincode : null);
  const serviceable = serviceabilityQuery.data?.is_serviceable ?? false;
  // A failed lookup must not lock the customer out of the form: the quote is
  // re-priced server-side before payment anyway, so degrade to "carry on".
  const lookupFailed = serviceabilityQuery.isError;
  const showRest = pincodeComplete && (serviceable || lookupFailed);

  useEffect(() => {
    onPincodeChange(pincodeComplete ? pincode : null);
  }, [onPincodeChange, pincode, pincodeComplete]);

  // Choosing a saved address fills every field, including the PIN code, which
  // re-triggers the serviceability lookup exactly as typing it would.
  useEffect(() => {
    if (selectedAddressId === NEW_ADDRESS) return;
    const chosen = saved.find((entry) => entry.id === selectedAddressId);
    if (!chosen) return;
    form.reset({
      name: chosen.name,
      phone: chosen.phone.replace(/^\+91/, ''),
      line1: chosen.line1,
      line2: chosen.line2 ?? '',
      city: chosen.city,
      state: chosen.state,
      pincode: chosen.pincode,
    });
  }, [form, saved, selectedAddressId]);

  const errorFor = (field: keyof AddressFormValues): string | undefined =>
    formState.errors[field]?.message ?? serverFieldErrors[field];

  return (
    <form
      id={formId}
      noValidate
      onSubmit={(event) => {
        void handleSubmit(onValidSubmit)(event);
      }}
      className="flex flex-col gap-4"
    >
      {savedLoading ? (
        <SkeletonGroup label="Loading your saved addresses">
          <Skeleton className="h-20 w-full" />
        </SkeletonGroup>
      ) : saved.length > 0 ? (
        <RadioGroup legend="Deliver to">
          {saved.map((entry) => (
            <OptionCard
              key={entry.id}
              name="saved-address"
              value={entry.id}
              checked={selectedAddressId === entry.id}
              onChange={() => onSelectAddress(entry.id)}
              label={entry.name}
              description={`${entry.line1}, ${entry.city} ${entry.pincode}`}
            />
          ))}
          <OptionCard
            name="saved-address"
            value={NEW_ADDRESS}
            checked={selectedAddressId === NEW_ADDRESS}
            onChange={() => {
              onSelectAddress(NEW_ADDRESS);
              form.reset({
                name: defaultName,
                phone: defaultPhone.replace(/^\+91/, ''),
                line1: '',
                line2: '',
                city: '',
                state: '',
                pincode: '',
              });
            }}
            label="Use a new address"
          />
        </RadioGroup>
      ) : null}

      <Card title="Where are we delivering?">
        <Input
          {...register('pincode', {
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              // Digits only, six max: a PIN code box that accepts letters is a
              // guaranteed 422 and an avoidable round trip.
              setValue('pincode', normalisePincode(event.target.value), {
                shouldValidate: false,
              });
            },
          })}
          label="PIN code"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={6}
          required
          disabled={disabled}
          hint="We check delivery before asking for the rest."
          error={errorFor('pincode')}
        />
      </Card>

      {/* Directly under the delivery answer, because that is the number it
          qualifies. Renders nothing during printing hours. */}
      <PrintingHoursNotice />

      <PincodeLookup
        enabled={pincodeComplete}
        serviceability={serviceabilityQuery.data}
        isLoading={serviceabilityQuery.isLoading}
        error={serviceabilityQuery.error}
      />

      {pincodeComplete && !serviceable && !lookupFailed && !serviceabilityQuery.isLoading ? (
        <p className="text-sm text-ink-muted">
          Try a different PIN code above, or contact us and we will see what we can do.
        </p>
      ) : null}

      {showRest ? (
        <Card title="Delivery address">
          <div className="flex flex-col gap-4">
            {serverFieldErrors['__all__'] ? (
              <Alert severity="danger" title="Please check these details" live>
                <p>{serverFieldErrors['__all__']}</p>
              </Alert>
            ) : null}

            <Input
              {...register('name')}
              label="Full name"
              autoComplete="name"
              required
              disabled={disabled}
              error={errorFor('name')}
            />
            <Input
              {...register('phone')}
              label="Mobile number"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              prefix="+91"
              maxLength={13}
              required
              disabled={disabled}
              hint="For delivery updates from the courier."
              error={errorFor('phone')}
            />
            <Input
              {...register('line1')}
              label="Flat / house number and street"
              autoComplete="address-line1"
              required
              disabled={disabled}
              error={errorFor('line1')}
            />
            <Input
              {...register('line2')}
              label="Area or landmark (optional)"
              hint="Locality, and anything that helps find the door."
              autoComplete="address-line2"
              disabled={disabled}
              error={errorFor('line2')}
            />
            <Input
              {...register('city')}
              label="City or town"
              autoComplete="address-level2"
              required
              disabled={disabled}
              error={errorFor('city')}
            />
            <Input
              {...register('state')}
              label="State"
              autoComplete="address-level1"
              required
              disabled={disabled}
              error={errorFor('state')}
            />

            {/* Required, and asked for LAST — after the typed lines, because
                the map is easier to place once the customer has just told us
                the area, and because a map at the top of a form reads as the
                whole task. */}
            <div className="border-t border-line pt-4">
              <LocationPicker
                value={location}
                onChange={onLocationChange}
                approxCentre={
                  serviceabilityQuery.data?.approx_latitude != null &&
                  serviceabilityQuery.data.approx_longitude != null
                    ? {
                        lat: serviceabilityQuery.data.approx_latitude,
                        lng: serviceabilityQuery.data.approx_longitude,
                      }
                    : null
                }
              />
            </div>

            {selectedAddressId === NEW_ADDRESS ? (
              <p className="text-sm text-ink-subtle">
                We save this address to your account so you do not have to type it again.
              </p>
            ) : null}
          </div>
        </Card>
      ) : null}
    </form>
  );
}
